import type { Database } from 'sql.js';
import { createCorrelationId } from '@core/domain/utils/correlationId.ts';
import type {
  BeneficiarioOption,
  PendingReposicionRow,
  ReposicionEditRecord,
  ReposicionExportRow,
  ReposicionFormCatalog,
  ReposicionListItem,
  ReposicionProductoCantidad,
  ReposicionProductoOption,
  SaveReposicionInput,
} from '../../domain/models/ReposicionCatalog.ts';
import type {
  ReposicionCatalogRepositoryPort,
  ReposicionRepositoryPort,
} from '../../domain/ports/ReposicionPorts.ts';
import {
  inClausePlaceholders,
  markRowFailed,
  markRowSyncWarning,
  markRowsSynced,
  normalizeSyncStatus,
  queryLocalBeneficiarios,
  queryPublicIdNombreTable,
  readInt,
  readString,
  runSqlJsTransaction,
  type GetSqlJsDatabase,
} from '@src/modules/platform/local-sqlite/infrastructure/sqlJsRowHelpers.ts';
import { markLocalSqliteDirty } from '@src/modules/platform/local-sqlite/infrastructure/localSqlitePersistDirty.ts';

export class SqlJsReposicionAdapter implements ReposicionCatalogRepositoryPort, ReposicionRepositoryPort {
  constructor(private readonly getDb: GetSqlJsDatabase) {}

  async loadFormCatalog(): Promise<ReposicionFormCatalog> {
    const core = await this.loadFormCatalogCore();
    const extras = await this.loadFormCatalogFormExtras();
    return { ...core, ...extras };
  }

  async loadFormCatalogCore(): Promise<Pick<ReposicionFormCatalog, 'items' | 'comunas'>> {
    const db = this.getDb();
    if (!db) return { items: [], comunas: [] };
    return {
      items: queryPublicIdNombreTable(db, 'items_entrega'),
      comunas: queryPublicIdNombreTable(db, 'comunas'),
    };
  }

  async loadFormCatalogFormExtras(): Promise<
    Pick<ReposicionFormCatalog, 'productos' | 'mediosReporte' | 'motivosReposicion' | 'parentescos'>
  > {
    const db = this.getDb();
    if (!db) return { productos: [], mediosReporte: [], motivosReposicion: [], parentescos: [] };
    return {
      productos: this.queryProductosReposicion(db),
      mediosReporte: queryPublicIdNombreTable(db, 'medios_reporte'),
      motivosReposicion: queryPublicIdNombreTable(db, 'motivos_reposicion'),
      parentescos: queryPublicIdNombreTable(db, 'parentescos'),
    };
  }

  async searchBeneficiariosByDocumento(query: string, itemPublicId = ''): Promise<BeneficiarioOption[]> {
    const db = this.getDb();
    if (!db) return [];
    return queryLocalBeneficiarios(db, query, itemPublicId).map((row) => ({
      publicId: readString(row, 'public_id'),
      documento: readString(row, 'documento'),
      nombre: readString(row, 'nombre'),
    }));
  }

  async save(input: SaveReposicionInput): Promise<{ publicId: string }> {
    const db = this.getDb();
    if (!db) throw new Error('Base de datos local no disponible.');
    const publicId = createCorrelationId();
    runSqlJsTransaction(db, () => {
      db.run(
        `INSERT INTO reposiciones_productos (
          public_id, comuna_public_id, direccion, fecha, beneficiario_public_id,
          medio_reporte_public_id, motivo_reposicion_public_id, recibe_otra_persona,
          parentesco_public_id, documento_acudiente, nombre_recibe, observaciones,
          firma_media_id, foto_media_id,
          sync_status, sync_error, fecha_creacion, fecha_actualizacion
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'pending', NULL, datetime('now'), datetime('now'))`,
        [
          publicId,
          input.comunaPublicId,
          input.direccion,
          input.fecha,
          input.beneficiarioPublicId,
          input.medioReportePublicId,
          input.motivoReposicionPublicId,
          input.recibeOtraPersona ? 1 : 0,
          input.parentescoPublicId,
          input.documentoAcudiente,
          input.nombreRecibe,
          input.observaciones,
          input.firmaMediaId,
          input.fotoMediaId,
        ],
      );
      for (const item of input.productos) {
        if (!item.productoPublicId.trim() || item.cantidad < 1) continue;
        db.run(
          `INSERT INTO reposicion_producto_item (reposicion_public_id, producto_public_id, cantidad) VALUES (?, ?, ?)`,
          [publicId, item.productoPublicId, item.cantidad],
        );
      }
    });
    return { publicId };
  }

  async getForEdit(publicId: string): Promise<ReposicionEditRecord | null> {
    const db = this.getDb();
    const id = publicId.trim();
    if (!db || !id) return null;
    const stmt = db.prepare(
      `SELECT r.comuna_public_id, r.direccion, r.fecha, r.beneficiario_public_id,
              r.medio_reporte_public_id, r.motivo_reposicion_public_id, r.recibe_otra_persona,
              r.parentesco_public_id, r.documento_acudiente, r.nombre_recibe, r.observaciones,
              r.firma_media_id, r.foto_media_id,
              b.documento AS beneficiario_documento, b.nombre AS beneficiario_nombre
       FROM reposiciones_productos r
       LEFT JOIN beneficiarios b ON b.public_id = r.beneficiario_public_id
       WHERE r.public_id = ?`,
    );
    stmt.bind([id]);
    if (!stmt.step()) {
      stmt.free();
      return null;
    }
    const row = stmt.getAsObject() as Record<string, unknown>;
    stmt.free();
    const productos = this.productosByReposicion(db, [id]).get(id) ?? [];
    return {
      publicId: id,
      comunaPublicId: readString(row, 'comuna_public_id'),
      direccion: readString(row, 'direccion'),
      fecha: readString(row, 'fecha'),
      beneficiarioPublicId: readString(row, 'beneficiario_public_id'),
      beneficiarioDocumento: readString(row, 'beneficiario_documento'),
      beneficiarioNombre: readString(row, 'beneficiario_nombre'),
      productos,
      medioReportePublicId: readString(row, 'medio_reporte_public_id'),
      motivoReposicionPublicId: readString(row, 'motivo_reposicion_public_id'),
      recibeOtraPersona: readInt(row, 'recibe_otra_persona') === 1,
      parentescoPublicId: readString(row, 'parentesco_public_id') || null,
      documentoAcudiente: readString(row, 'documento_acudiente') || null,
      nombreRecibe: readString(row, 'nombre_recibe') || null,
      observaciones: readString(row, 'observaciones'),
      firmaMediaId: readString(row, 'firma_media_id') || null,
      fotoMediaId: readString(row, 'foto_media_id') || null,
    };
  }

  async update(publicId: string, input: SaveReposicionInput): Promise<boolean> {
    const db = this.getDb();
    const id = publicId.trim();
    if (!db || !id) return false;
    db.run('BEGIN');
    try {
      db.run(
        `UPDATE reposiciones_productos SET
          comuna_public_id = ?, direccion = ?, fecha = ?, beneficiario_public_id = ?,
          medio_reporte_public_id = ?, motivo_reposicion_public_id = ?, recibe_otra_persona = ?,
          parentesco_public_id = ?, documento_acudiente = ?, nombre_recibe = ?, observaciones = ?,
          firma_media_id = ?, foto_media_id = ?, sync_status = 'pending', sync_error = NULL,
          fecha_actualizacion = datetime('now')
         WHERE public_id = ?`,
        [
          input.comunaPublicId,
          input.direccion,
          input.fecha,
          input.beneficiarioPublicId,
          input.medioReportePublicId,
          input.motivoReposicionPublicId,
          input.recibeOtraPersona ? 1 : 0,
          input.parentescoPublicId,
          input.documentoAcudiente,
          input.nombreRecibe,
          input.observaciones,
          input.firmaMediaId,
          input.fotoMediaId,
          id,
        ],
      );
      const changed = db.getRowsModified() > 0;
      if (!changed) {
        db.run('ROLLBACK');
        return false;
      }
      db.run(`DELETE FROM reposicion_producto_item WHERE reposicion_public_id = ?`, [id]);
      for (const item of input.productos) {
        if (!item.productoPublicId.trim() || item.cantidad < 1) continue;
        db.run(
          `INSERT INTO reposicion_producto_item (reposicion_public_id, producto_public_id, cantidad) VALUES (?, ?, ?)`,
          [id, item.productoPublicId, item.cantidad],
        );
      }
      db.run('COMMIT');
      markLocalSqliteDirty();
      return true;
    } catch (e) {
      db.run('ROLLBACK');
      throw e;
    }
  }

  async patchReposicionDeliveryMedia(
    publicId: string,
    patch: { firmaMediaId?: string; fotoMediaId?: string },
  ): Promise<boolean> {
    const db = this.getDb();
    const id = publicId.trim();
    if (!db || !id) return false;
    const record = await this.getForEdit(id);
    if (!record) return false;
    const firmaMediaId = patch.firmaMediaId?.trim() ?? record.firmaMediaId;
    const fotoMediaId = patch.fotoMediaId?.trim() ?? record.fotoMediaId;
    db.run(
      `UPDATE reposiciones_productos SET
        firma_media_id = ?, foto_media_id = ?, sync_status = 'pending', sync_error = NULL,
        fecha_actualizacion = datetime('now')
       WHERE public_id = ?`,
      [firmaMediaId, fotoMediaId, id],
    );
    if (db.getRowsModified() === 0) return false;
    markLocalSqliteDirty();
    return true;
  }

  async listReposiciones(limit = 20): Promise<ReposicionListItem[]> {
    const db = this.getDb();
    if (!db) return [];
    const max = Math.max(1, Math.min(limit, 100));
    const draft = this.queryListReposicionRows(db, { limit: max });
    const labels = this.productosLabelsByReposicion(db, draft.map((r) => r.publicId));
    return draft.map((row) => ({
      ...row,
      productosLabel: labels.get(row.publicId) ?? '—',
    }));
  }

  async listUnsynced(): Promise<ReposicionListItem[]> {
    const db = this.getDb();
    if (!db) return [];
    const draft = this.queryListReposicionRows(db, { unsyncedOnly: true });
    const labels = this.productosLabelsByReposicion(db, draft.map((r) => r.publicId));
    return draft.map((row) => ({
      ...row,
      productosLabel: labels.get(row.publicId) ?? '—',
    }));
  }

  private queryListReposicionRows(
    db: Database,
    opts: { unsyncedOnly?: boolean; limit?: number },
  ): Array<Omit<ReposicionListItem, 'productosLabel'> & { publicId: string }> {
    const where = opts.unsyncedOnly ? `WHERE r.sync_status != 'synced'` : '';
    const order = opts.unsyncedOnly ? 'ASC' : 'DESC';
    const limitClause = opts.limit != null ? ' LIMIT ?' : '';
    const stmt = db.prepare(
      `SELECT r.public_id, r.fecha, r.direccion, r.sync_status, r.sync_error, r.recibe_otra_persona,
              r.documento_acudiente, r.nombre_recibe, r.firma_media_id, r.foto_media_id,
              c.nombre AS comuna_nombre, b.nombre AS beneficiario_nombre, b.documento AS beneficiario_documento,
              m.nombre AS medio_nombre, mot.nombre AS motivo_nombre
       FROM reposiciones_productos r
       LEFT JOIN comunas c ON c.public_id = r.comuna_public_id
       LEFT JOIN beneficiarios b ON b.public_id = r.beneficiario_public_id
       LEFT JOIN medios_reporte m ON m.public_id = r.medio_reporte_public_id
       LEFT JOIN motivos_reposicion mot ON mot.public_id = r.motivo_reposicion_public_id
       ${where}
       ORDER BY r.id ${order}${limitClause}`,
    );
    if (opts.limit != null) stmt.bind([opts.limit]);
    const draft: Array<Omit<ReposicionListItem, 'productosLabel'> & { publicId: string }> = [];
    while (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      const publicId = readString(row, 'public_id');
      const syncStatus = readString(row, 'sync_status');
      const recibeOtraPersona = readInt(row, 'recibe_otra_persona') === 1;
      const nombreRecibe = readString(row, 'nombre_recibe');
      draft.push({
        publicId,
        fecha: readString(row, 'fecha'),
        comunaNombre: readString(row, 'comuna_nombre') || '—',
        direccion: readString(row, 'direccion'),
        beneficiarioNombre: readString(row, 'beneficiario_nombre') || '—',
        beneficiarioDocumento: readString(row, 'beneficiario_documento') || '—',
        medioNombre: readString(row, 'medio_nombre') || '—',
        motivoNombre: readString(row, 'motivo_nombre') || '—',
        recibeOtraPersona,
        acudienteLabel: recibeOtraPersona ? nombreRecibe || '—' : 'Beneficiario',
        firmaMediaId: readString(row, 'firma_media_id') || null,
        fotoMediaId: readString(row, 'foto_media_id') || null,
        syncStatus: normalizeSyncStatus(syncStatus),
        syncError: readString(row, 'sync_error') || null,
      });
    }
    stmt.free();
    return draft;
  }

  async listPendingPush(): Promise<PendingReposicionRow[]> {
    const rows = await this.loadPushRows(`WHERE r.sync_status IN ('pending', 'failed')`, 'ASC');
    return rows.map(({ syncStatus: _syncStatus, syncError: _syncError, ...row }) => row);
  }

  async listAllForExport(limit = 500): Promise<ReposicionExportRow[]> {
    const max = Math.max(1, Math.min(limit, 500));
    return this.loadPushRows('', 'DESC', max);
  }

  private loadPushRows(
    whereClause: string,
    order: 'ASC' | 'DESC',
    limit?: number,
  ): ReposicionExportRow[] {
    const db = this.getDb();
    if (!db) return [];
    const limitClause = limit != null ? ' LIMIT ?' : '';
    const stmt = db.prepare(
      `SELECT r.public_id, r.comuna_public_id, r.direccion, r.fecha, r.beneficiario_public_id,
              r.medio_reporte_public_id, r.motivo_reposicion_public_id, r.recibe_otra_persona,
              r.parentesco_public_id, r.documento_acudiente, r.nombre_recibe, r.observaciones,
              r.firma_media_id, r.foto_media_id, r.sync_status, r.sync_error,
              b.documento AS beneficiario_documento, b.nombre AS beneficiario_nombre
       FROM reposiciones_productos r
       JOIN beneficiarios b ON b.public_id = r.beneficiario_public_id
       ${whereClause} ORDER BY r.id ${order}${limitClause}`,
    );
    if (limit != null) stmt.bind([limit]);
    const draft: Array<
      Omit<ReposicionExportRow, 'productos'> & { publicId: string }
    > = [];
    while (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      const publicId = readString(row, 'public_id');
      draft.push({
        publicId,
        comunaPublicId: readString(row, 'comuna_public_id'),
        direccion: readString(row, 'direccion'),
        fecha: readString(row, 'fecha'),
        beneficiarioPublicId: readString(row, 'beneficiario_public_id'),
        beneficiarioDocumento: readString(row, 'beneficiario_documento'),
        beneficiarioNombre: readString(row, 'beneficiario_nombre'),
        medioReportePublicId: readString(row, 'medio_reporte_public_id'),
        motivoReposicionPublicId: readString(row, 'motivo_reposicion_public_id'),
        recibeOtraPersona: readInt(row, 'recibe_otra_persona') === 1,
        parentescoPublicId: readString(row, 'parentesco_public_id') || null,
        documentoAcudiente: readString(row, 'documento_acudiente') || null,
        nombreRecibe: readString(row, 'nombre_recibe') || null,
        observaciones: readString(row, 'observaciones'),
        firmaMediaId: readString(row, 'firma_media_id') || null,
        fotoMediaId: readString(row, 'foto_media_id') || null,
        syncStatus: normalizeSyncStatus(readString(row, 'sync_status')),
        syncError: readString(row, 'sync_error') || null,
      });
    }
    stmt.free();
    const rows: ReposicionExportRow[] = [];
    const productos = this.productosByReposicion(db, draft.map((r) => r.publicId));
    for (const row of draft) {
      rows.push({ ...row, productos: productos.get(row.publicId) ?? [] });
    }
    return rows;
  }

  async markSynced(publicIds: string[]): Promise<void> {
    const db = this.getDb();
    if (!db) return;
    markRowsSynced(db, 'reposiciones_productos', publicIds);
  }

  async markFailed(publicId: string, message: string): Promise<void> {
    const db = this.getDb();
    if (!db) return;
    markRowFailed(db, 'reposiciones_productos', publicId, message);
  }

  async setSyncWarning(publicId: string, message: string): Promise<void> {
    const db = this.getDb();
    if (!db) return;
    markRowSyncWarning(db, 'reposiciones_productos', publicId, message);
  }

  async clearSyncError(publicId: string): Promise<void> {
    const db = this.getDb();
    const id = publicId.trim();
    if (!db || !id) return;
    db.run(
      `UPDATE reposiciones_productos SET sync_status = 'pending', sync_error = NULL, fecha_actualizacion = datetime('now') WHERE public_id = ?`,
      [id],
    );
  }

  private productosLabelsByReposicion(db: Database, reposicionPublicIds: string[]): Map<string, string> {
    const map = new Map<string, string>();
    if (reposicionPublicIds.length === 0) return map;
    const stmt = db.prepare(
      `SELECT i.reposicion_public_id, p.nombre, i.cantidad
       FROM reposicion_producto_item i
       JOIN productos_reposicion p ON p.public_id = i.producto_public_id
       WHERE i.reposicion_public_id IN (${inClausePlaceholders(reposicionPublicIds.length)})
       ORDER BY p.nombre COLLATE NOCASE`,
    );
    stmt.bind(reposicionPublicIds);
    const partsByReposicion = new Map<string, string[]>();
    while (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      const reposicionId = readString(row, 'reposicion_public_id');
      const nombre = readString(row, 'nombre');
      const cantidad = Math.max(1, readInt(row, 'cantidad'));
      if (!reposicionId || !nombre) continue;
      const parts = partsByReposicion.get(reposicionId) ?? [];
      parts.push(`${nombre} ×${cantidad}`);
      partsByReposicion.set(reposicionId, parts);
    }
    stmt.free();
    for (const [id, parts] of partsByReposicion) map.set(id, parts.join(', '));
    return map;
  }

  private productosByReposicion(db: Database, reposicionPublicIds: string[]): Map<string, ReposicionProductoCantidad[]> {
    const map = new Map<string, ReposicionProductoCantidad[]>();
    if (reposicionPublicIds.length === 0) return map;
    const stmt = db.prepare(
      `SELECT reposicion_public_id, producto_public_id, cantidad
       FROM reposicion_producto_item
       WHERE reposicion_public_id IN (${inClausePlaceholders(reposicionPublicIds.length)})`,
    );
    stmt.bind(reposicionPublicIds);
    while (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      const reposicionId = readString(row, 'reposicion_public_id');
      const productoPublicId = readString(row, 'producto_public_id');
      if (!reposicionId || !productoPublicId) continue;
      const items = map.get(reposicionId) ?? [];
      items.push({
        productoPublicId,
        cantidad: Math.max(1, readInt(row, 'cantidad')),
      });
      map.set(reposicionId, items);
    }
    stmt.free();
    return map;
  }

  private queryProductosReposicion(db: Database): ReposicionProductoOption[] {
    const stmt = db.prepare(
      `SELECT pr.public_id, pr.nombre, pri.item_entrega_public_id
       FROM productos_reposicion pr
       LEFT JOIN producto_reposicion_item pri ON pri.producto_reposicion_public_id = pr.public_id
       WHERE pr.estado = 1
       ORDER BY pr.nombre COLLATE NOCASE, pri.item_entrega_public_id`,
    );
    const byId = new Map<string, ReposicionProductoOption>();
    while (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      const publicId = readString(row, 'public_id');
      if (!publicId) continue;
      let producto = byId.get(publicId);
      if (!producto) {
        producto = { publicId, nombre: readString(row, 'nombre'), itemsPublicIds: [] };
        byId.set(publicId, producto);
      }
      const itemId = readString(row, 'item_entrega_public_id');
      if (itemId) producto.itemsPublicIds.push(itemId);
    }
    stmt.free();
    return [...byId.values()];
  }
}
